import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const useNavigationGuard = (saveEnable, setSaveEnable, message) => {
  const navigate = useNavigate();
  const location = useLocation();
  const saveEnabledRef = useRef(saveEnable);
  const confirmPromptRef = useRef(false); // To track if confirmation prompt is already shown

  useEffect(() => {
    saveEnabledRef.current = saveEnable;
  }, [saveEnable]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (saveEnabledRef.current) {
        event.preventDefault();
        event.returnValue = message; // This is required for the prompt to appear in some browsers
      }
    };

    const handlePopState = (event) => {
      if (saveEnabledRef.current && !confirmPromptRef.current) {
        if (!window.confirm(message)) {
          event.preventDefault();
          navigate(location.pathname); // Navigate back to current path
          confirmPromptRef.current = true; // Set flag to prevent further prompts
        } else {
          confirmPromptRef.current = false; // Reset flag if user confirms
        }
      }
    };

    const handleBeforeRouteChange = (event) => {
      if (saveEnabledRef.current && !confirmPromptRef.current) {
        if (!window.confirm(message)) {
          event.preventDefault();
          confirmPromptRef.current = true; // Set flag to prevent further prompts
        } else {
          confirmPromptRef.current = false; // Reset flag if user confirms
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function () {
      const result = originalPushState.apply(this, arguments);
      window.dispatchEvent(new Event('pushstate'));
      return result;
    };

    window.history.replaceState = function () {
      const result = originalReplaceState.apply(this, arguments);
      window.dispatchEvent(new Event('replacestate'));
      return result;
    };

    window.addEventListener('pushstate', handleBeforeRouteChange);
    window.addEventListener('replacestate', handleBeforeRouteChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('pushstate', handleBeforeRouteChange);
      window.removeEventListener('replacestate', handleBeforeRouteChange);

      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;

      confirmPromptRef.current = false; // Reset the confirmation prompt flag on unmount
    };
  }, [saveEnable, message, navigate, location]);

  // Optional: Reset saveEnable when component unmounts
  useEffect(() => {
    return () => {
      if (typeof setSaveEnable === 'function') {
        setSaveEnable(false);
      }
    };
  }, [setSaveEnable]);
};

export default useNavigationGuard;
